import * as React from "react";
import { cn } from "@/lib/utils";
import { Calendar } from "lucide-react";

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Special handling for date type inputs
    if (type === "date") {
      return (
        <div className="relative">
          <input
            type={type}
            className={cn(
              "flex h-16 w-full rounded-md border border-input bg-transparent px-3 text-lg ring-offset-background",
              "file:border-0 file:bg-transparent file:text-sm file:font-medium",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "[&::-webkit-datetime-edit-fields-wrapper]:text-black", // Changed to black for input value
              "[&::-webkit-datetime-edit]:px-2",
              "[&::-webkit-datetime-edit-text]:text-black", // Changed to black for separators
              "[&::-webkit-calendar-picker-indicator]:hidden",
              "placeholder:text-gray-500", // Using placeholder: instead of placeholder-shown
              "text-black", // Default text color for the input
              "pr-12",
              className
            )}
            ref={inputRef}
            {...props}
          />
          <button
            type="button"
            onClick={() => {
              if (inputRef.current) {
                inputRef.current.showPicker();
                inputRef.current.focus(); // Keep focus on input
              }
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Open date picker"
          >
            <Calendar size={24} />
          </button>
        </div>
      );
    }

    // Default styling for all other input types
    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-transparent px-3 text-base ring-offset-background",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          "md:text-sm",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

export { Input };